import { STATEMENT_IMPORT_ERROR_CODES_V2 } from "@tradvera/contracts";
import type { MyPortfolioNextAccount } from "../client.js";

export class StatementImportClientErrorV2 extends Error {
  constructor(public readonly code: string, public readonly status?: number) {
    super(code); this.name = "StatementImportClientErrorV2";
  }
}

export function createStatementRequestV2(account: Pick<MyPortfolioNextAccount, "getIdToken">,
  adminSession: string | undefined, fetchImpl: typeof fetch) {
  const response = async (url: string, init: RequestInit): Promise<Response> => {
    const token = await account.getIdToken();
    const headers = new Headers(init.headers);
    headers.set("authorization", `Bearer ${token}`);
    if (adminSession) headers.set("x-tradvera-admin-session", adminSession);
    let result: Response;
    try { result = await fetchImpl(url, { ...init, headers }); }
    catch (error) { throw error; }
    if (!result.ok) throw await statementResponseErrorV2(result);
    return result;
  };
  return {
    response,
    async json(url: string, init: RequestInit): Promise<unknown> {
      const result = await response(url, init);
      try { return await result.json() as unknown; }
      catch { throw new StatementImportClientErrorV2("STATEMENT_IMPORT_RESPONSE_INVALID", result.status); }
    },
  };
}

export async function statementResponseErrorV2(response: Response): Promise<StatementImportClientErrorV2> {
  let code = `STATEMENT_IMPORT_HTTP_${response.status}`;
  try {
    const value = await response.json() as unknown;
    if (value && typeof value === "object" && !Array.isArray(value)) {
      const source = value as { code?: unknown; error?: unknown };
      const candidate = typeof source.code === "string" ? source.code : source.error;
      if (typeof candidate === "string" && (STATEMENT_IMPORT_ERROR_CODES_V2.includes(
        candidate as typeof STATEMENT_IMPORT_ERROR_CODES_V2[number])
        || /^(?:ADMIN_SESSION_(?:REQUIRED|EXPIRED)|ADMIN_STEP_UP_NOT_FRESH|PIN_(?:INVALID|LOCKED)|PIN_CHANGE_REQUIRED)$/u.test(candidate))) {
        code = candidate;
      }
    }
  } catch { /* HTTP status remains authoritative. */ }
  return new StatementImportClientErrorV2(code, response.status);
}

export function exactStatementObjectKeysV2(value: unknown, keys: readonly string[]): value is Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const source = value as Record<string, unknown>; const expected = new Set(keys);
  return Object.keys(source).length === expected.size && Object.keys(source).every((key) => expected.has(key));
}

export function invalidStatementResponseV2(status?: number): never {
  throw new StatementImportClientErrorV2("STATEMENT_IMPORT_RESPONSE_INVALID", status);
}
